import React, { useEffect, useState } from 'react';

// MUI
import { Box, InputLabel, Input, Checkbox, TextField, Tooltip, Typography } from '@mui/material';

// Styles
import '../AddPostageModal/styles.scss';
import OLCBaseModal from '../../BaseModal';
import DatePicker from '~/components/General/OlcDatePicker';
import { CheckedCIcon, ContactInfoCIcon, UncheckedCIcon } from '~/assets/images';
import { dateProtector, parseLocalDateOnly } from '~/utils/date-format';

const EditPostageModal = ({ open, handleClose, initialValues = {}, onSubmit, loading = false }) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const [values, setValues] = useState({
    postcard: '',
    letter: '',
    standard: '',
    forever: '',
    firstClassFlat: '',
    standardClassFlat: '',
    postcardIncrease: '',
    letterIncrease: '',
    standardIncrease: '',
    foreverIncrease: '',
    firstClassFlatIncrease: '',
    standardClassFlatIncrease: '',
    updatePostcard: false,
    updateLetter: false,
    updateStandard: false,
    updateForever: false,
    updateFirstClassFlat: false,
    updateStandardClassFlat: false,
    effectiveDate: null,
    notes: '',
  });
  const [errors, setErrors] = useState({});

  const showAdditionalIncrease =
    values.updatePostcard ||
    values.updateLetter ||
    values.updateStandard ||
    values.updateForever ||
    values.updateFirstClassFlat ||
    values.updateStandardClassFlat;

  useEffect(() => {
    if (open) {
      let effectiveDate = null;
      if (initialValues.effectiveDate) {
        // initialValues.effectiveDate is already a correctly-parsed local Date
        // (parsed upstream via parseLocalDateOnly); just clone it defensively
        // in case a caller ever passes the raw "YYYY-MM-DD" string instead.
        effectiveDate =
          initialValues.effectiveDate instanceof Date
            ? new Date(initialValues.effectiveDate)
            : parseLocalDateOnly(initialValues.effectiveDate);
      }
      setValues({
        postcard: initialValues.postcard ?? '',
        letter: initialValues.letter ?? '',
        standard: initialValues.standard ?? '',
        forever: initialValues.forever ?? '',
        firstClassFlat: initialValues.firstClassFlat ?? '',
        standardClassFlat: initialValues.standardClassFlat ?? '',
        postcardIncrease: initialValues.postcardIncrease ?? '',
        letterIncrease: initialValues.letterIncrease ?? '',
        standardIncrease: initialValues.standardIncrease ?? '',
        foreverIncrease: initialValues.foreverIncrease ?? '',
        firstClassFlatIncrease: initialValues.firstClassFlatIncrease ?? '',
        standardClassFlatIncrease: initialValues.standardClassFlatIncrease ?? '',
        updatePostcard: !!initialValues.updatePostcard,
        updateLetter: !!initialValues.updateLetter,
        updateStandard: !!initialValues.updateStandard,
        updateForever: !!initialValues.updateForever,
        updateFirstClassFlat: !!initialValues.updateFirstClassFlat,
        updateStandardClassFlat: !!initialValues.updateStandardClassFlat,
        effectiveDate: effectiveDate,
        notes: initialValues.notes ?? '',
      });
      setErrors({});
    }
  }, [open, initialValues]);

  // Validation function for numeric fields
  const validateNumericField = (value, fieldName) => {
    if (value === '' || value === null || value === undefined) {
      return ''; // Empty is allowed
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return 'Must be a valid number';
    }

    if (numValue <= 0) {
      return 'Must be greater than 0';
    }

    return '';
  };

  const handleChange = (key) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;

    // Map of checkbox keys to their corresponding increase field keys
    const checkboxToIncreaseMap = {
      updatePostcard: 'postcardIncrease',
      updateLetter: 'letterIncrease',
      updateStandard: 'standardIncrease',
      updateForever: 'foreverIncrease',
      updateFirstClassFlat: 'firstClassFlatIncrease',
      updateStandardClassFlat: 'standardClassFlatIncrease',
    };

    // If unchecking a checkbox, clear the corresponding increase field
    if (e?.target?.type === 'checkbox' && checkboxToIncreaseMap[key] && !value) {
      setValues((prev) => ({
        ...prev,
        [key]: value,
        [checkboxToIncreaseMap[key]]: '',
      }));
      // Also clear the error for the increase field
      setErrors((prev) => ({ ...prev, [checkboxToIncreaseMap[key]]: '' }));
    } else {
      setValues((prev) => ({ ...prev, [key]: value }));
    }

    // Validate numeric fields
    const numericFields = [
      'postcard',
      'letter',
      'standard',
      'forever',
      'firstClassFlat',
      'standardClassFlat',
      'postcardIncrease',
      'letterIncrease',
      'standardIncrease',
      'foreverIncrease',
      'firstClassFlatIncrease',
      'standardClassFlatIncrease',
    ];
    if (numericFields.includes(key) && e?.target?.type !== 'checkbox') {
      const error = validateNumericField(value, key);
      setErrors((prev) => ({ ...prev, [key]: error }));
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      dateProtector(date);
    }
    setValues((prev) => ({ ...prev, effectiveDate: date }));
  };

  // Check if form has validation errors
  const hasValidationErrors = () => {
    return Object.values(errors).some((error) => error !== '');
  };

  // Check if all required fields have data
  const hasRequiredFields = () => {
    const requiredFields = ['postcard', 'letter', 'standard', 'forever', 'effectiveDate'];
    return requiredFields.every((field) => {
      const value = values[field];
      if (field === 'effectiveDate') {
        return value !== null && value !== undefined;
      }
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Disable submit if validation errors OR missing required fields
  const isSubmitDisabled = () => {
    return hasValidationErrors() || !hasRequiredFields();
  };

  const handleSave = () => {
    // Validate all numeric fields before saving
    const numericFields = [
      'postcard',
      'letter',
      'standard',
      'forever',
      'firstClassFlat',
      'standardClassFlat',
      'postcardIncrease',
      'letterIncrease',
      'standardIncrease',
      'foreverIncrease',
      'firstClassFlatIncrease',
      'standardClassFlatIncrease',
    ];
    const newErrors = {};

    numericFields.forEach((field) => {
      const error = validateNumericField(values[field], field);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    // Only proceed if no validation errors
    if (Object.keys(newErrors).length === 0) {
      if (onSubmit) onSubmit(values);
    }
  };

  return (
    <OLCBaseModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSave}
      title="Edit Postage"
      subTitle="Update USPS postage cost and options."
      primaryText="Update"
      width={640}
      disableScrollLock={false}
      paperClassName="postageModalWrapper"
      loading={loading}
      disablePrimary={isSubmitDisabled()}
    >
      <Box className="postageModalContent">
        <Box className={`fieldsHeader withIncrease`}>
          <InputLabel className="fieldLabel">Mail Class</InputLabel>
          <Box className="updatePricingInfo">
            <InputLabel className="fieldLabel">Update Product</InputLabel>
            <Box className="pricingWithInfo">
              <InputLabel className="fieldLabel">Pricing</InputLabel>
              <Tooltip
                title={
                  <Typography className="tooltipText">
                    The postage difference will automatically be applied to your customer pricing.
                    You can add an additional increase if desired.
                  </Typography>
                }
                placement="bottom-start"
              >
                <img src={ContactInfoCIcon} alt="info" style={{ cursor: 'pointer' }} />
              </Tooltip>
            </Box>
          </Box>
          {showAdditionalIncrease && (
            <InputLabel className="fieldLabel">Add'l Increase (optional)</InputLabel>
          )}
        </Box>

        <InputLabel className="rowTopLabel">First-Class Postcard*</InputLabel>
        <Box className={`rowNew withIncrease`}>
          <Box className="productCell">
            <Box className="inputWithError">
              <Input
                type="number"
                className={`rowInput ${errors.postcard ? 'invalid' : ''}`}
                placeholder="$0.383"
                value={values.postcard}
                onChange={handleChange('postcard')}
              />
              {errors.postcard && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {errors.postcard}
                </Typography>
              )}
            </Box>
            <Checkbox
              className="rowCheck returnAddressCheck"
              checked={values.updatePostcard}
              onChange={handleChange('updatePostcard')}
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            />
            {showAdditionalIncrease && (
              <Box className="increaseCell">
                {values.updatePostcard ? (
                  <Box className="inputWithError">
                    <Input
                      type="number"
                      className={`rowInput ${errors.postcardIncrease ? 'invalid' : ''}`}
                      placeholder="Enter additional increase"
                      value={values.postcardIncrease}
                      onChange={handleChange('postcardIncrease')}
                    />
                    {errors.postcardIncrease && (
                      <Typography className="errorMessage">
                        <sup>*</sup>
                        {errors.postcardIncrease}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <span />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <InputLabel className="rowTopLabel">First-Class Letter*</InputLabel>
        <Box className={`rowNew withIncrease`}>
          <Box className="productCell">
            <Box className="inputWithError">
              <Input
                type="number"
                className={`rowInput ${errors.letter ? 'invalid' : ''}`}
                placeholder="$0.465"
                value={values.letter}
                onChange={handleChange('letter')}
              />
              {errors.letter && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {errors.letter}
                </Typography>
              )}
            </Box>
            <Checkbox
              className="rowCheck returnAddressCheck"
              checked={values.updateLetter}
              onChange={handleChange('updateLetter')}
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            />
            {showAdditionalIncrease && (
              <Box className="increaseCell">
                {values.updateLetter ? (
                  <Box className="inputWithError">
                    <Input
                      type="number"
                      className={`rowInput ${errors.letterIncrease ? 'invalid' : ''}`}
                      placeholder="Enter additional increase"
                      value={values.letterIncrease}
                      onChange={handleChange('letterIncrease')}
                    />
                    {errors.letterIncrease && (
                      <Typography className="errorMessage">
                        <sup>*</sup>
                        {errors.letterIncrease}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <span />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <InputLabel className="rowTopLabel">Standard Class*</InputLabel>
        <Box className={`rowNew  withIncrease`}>
          <Box className="productCell">
            <Box className="inputWithError">
              <Input
                type="number"
                className={`rowInput ${errors.standard ? 'invalid' : ''}`}
                placeholder="$0.340"
                value={values.standard}
                onChange={handleChange('standard')}
              />
              {errors.standard && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {errors.standard}
                </Typography>
              )}
            </Box>
            <Checkbox
              className="rowCheck returnAddressCheck"
              checked={values.updateStandard}
              onChange={handleChange('updateStandard')}
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            />
            {showAdditionalIncrease && (
              <Box className="increaseCell">
                {values.updateStandard ? (
                  <Box className="inputWithError">
                    <Input
                      type="number"
                      className={`rowInput ${errors.standardIncrease ? 'invalid' : ''}`}
                      placeholder="Enter additional increase"
                      value={values.standardIncrease}
                      onChange={handleChange('standardIncrease')}
                    />
                    {errors.standardIncrease && (
                      <Typography className="errorMessage">
                        <sup>*</sup>
                        {errors.standardIncrease}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <span />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <InputLabel className="rowTopLabel">First-Class Forever*</InputLabel>
        <Box className={`rowNew  withIncrease`}>
          <Box className="productCell">
            <Box className="inputWithError">
              <Input
                type="number"
                className={`rowInput ${errors.forever ? 'invalid' : ''}`}
                placeholder="$0.640"
                value={values.forever}
                onChange={handleChange('forever')}
              />
              {errors.forever && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {errors.forever}
                </Typography>
              )}
            </Box>
            <Checkbox
              className="rowCheck returnAddressCheck"
              checked={values.updateForever}
              onChange={handleChange('updateForever')}
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            />
            {showAdditionalIncrease && (
              <Box className="increaseCell">
                {values.updateForever ? (
                  <Box className="inputWithError">
                    <Input
                      type="number"
                      className={`rowInput ${errors.foreverIncrease ? 'invalid' : ''}`}
                      placeholder="Enter additional increase"
                      value={values.foreverIncrease}
                      onChange={handleChange('foreverIncrease')}
                    />
                    {errors.foreverIncrease && (
                      <Typography className="errorMessage">
                        <sup>*</sup>
                        {errors.foreverIncrease}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <span />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <InputLabel className="rowTopLabel">First Class Flat*</InputLabel>
        <Box className={`rowNew withIncrease`}>
          <Box className="productCell">
            <Box className="inputWithError">
              <Input
                type="number"
                className={`rowInput ${errors.firstClassFlat ? 'invalid' : ''}`}
                placeholder="$0.000"
                value={values.firstClassFlat}
                onChange={handleChange('firstClassFlat')}
              />
              {errors.firstClassFlat && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {errors.firstClassFlat}
                </Typography>
              )}
            </Box>
            <Checkbox
              className="rowCheck returnAddressCheck"
              checked={values.updateFirstClassFlat}
              onChange={handleChange('updateFirstClassFlat')}
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            />
            {showAdditionalIncrease && (
              <Box className="increaseCell">
                {values.updateFirstClassFlat ? (
                  <Box className="inputWithError">
                    <Input
                      type="number"
                      className={`rowInput ${errors.firstClassFlatIncrease ? 'invalid' : ''}`}
                      placeholder="Enter additional increase"
                      value={values.firstClassFlatIncrease}
                      onChange={handleChange('firstClassFlatIncrease')}
                    />
                    {errors.firstClassFlatIncrease && (
                      <Typography className="errorMessage">
                        <sup>*</sup>
                        {errors.firstClassFlatIncrease}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <span />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <InputLabel className="rowTopLabel">Standard Class Flat*</InputLabel>
        <Box className={`rowNew withIncrease`}>
          <Box className="productCell">
            <Box className="inputWithError">
              <Input
                type="number"
                className={`rowInput ${errors.standardClassFlat ? 'invalid' : ''}`}
                placeholder="$0.000"
                value={values.standardClassFlat}
                onChange={handleChange('standardClassFlat')}
              />
              {errors.standardClassFlat && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {errors.standardClassFlat}
                </Typography>
              )}
            </Box>
            <Checkbox
              className="rowCheck returnAddressCheck"
              checked={values.updateStandardClassFlat}
              onChange={handleChange('updateStandardClassFlat')}
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            />
            {showAdditionalIncrease && (
              <Box className="increaseCell">
                {values.updateStandardClassFlat ? (
                  <Box className="inputWithError">
                    <Input
                      type="number"
                      className={`rowInput ${errors.standardClassFlatIncrease ? 'invalid' : ''}`}
                      placeholder="Enter additional increase"
                      value={values.standardClassFlatIncrease}
                      onChange={handleChange('standardClassFlatIncrease')}
                    />
                    {errors.standardClassFlatIncrease && (
                      <Typography className="errorMessage">
                        <sup>*</sup>
                        {errors.standardClassFlatIncrease}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <span />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box className="divider" />

        <Box className="mailedDate newDatePicker updatedDesign">
          <Box className="selectDate">
            <InputLabel className="dateLabel newfieldsLabel">Effective Date*</InputLabel>
            <DatePicker
              minDate={tomorrow}
              onChange={handleDateChange}
              dateFormat="MM/dd/yyyy"
              className="datePicker"
              placeholderText="Select date"
              selected={values.effectiveDate}
              onBlur={(e) => {
                if (e.target.value == '') {
                  handleDateChange(null);
                }
              }}
            />
          </Box>
        </Box>

        <Box className="rowTopLabel">Notes</Box>
        <Box className="row">
          <TextField
            className="notesInput"
            multiline
            minRows={2}
            placeholder="Add any notes..."
            value={values.notes.trimStart()}
            onChange={handleChange('notes')}
          />
        </Box>
      </Box>
    </OLCBaseModal>
  );
};

export default EditPostageModal;
